import React, { useState } from 'react';
import { PhoneCall, Mail, MapPin, Send, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';
import Header from '../components/Header';
import Footer from '../components/Footer';
import usePageMeta from '../hooks/usePageMeta';

const Contact = () => {
  usePageMeta('Contact Alpha Epoxy Floors Sydney | Free Quote | 0451 945 455', 'Contact Alpha Epoxy Floors for a free quote. Call 0451 945 455 or fill out our form. Serving all Sydney suburbs. 24-hour response guaranteed.');
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.email) {
      toast({ title: "Missing Information", description: "Please fill in all required fields.", variant: "destructive" });
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, source: 'contact-page' })
      });
      if (!res.ok) throw new Error('Failed');
      toast({ title: "Message Sent!", description: "We'll get back to you within 24 hours." });
      if (window.gtag) { window.gtag('event', 'conversion', { 'send_to': 'AW-18269270249/IPghCNXa_sQcEOnhu4dE', 'value': 1.0, 'currency': 'AUD' }); }
      setFormData({ name: '', phone: '', email: '', message: '' });
    } catch (err) {
      toast({ title: "Error", description: "Something went wrong. Please call us at 0451 945 455.", variant: "destructive" });
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  return (
    <div className="min-h-screen bg-black">
      <Header activePage="contact" />

      <div className="pt-28">
        {/* Hero Section */}
        <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img src="/images/contact-hero.jpg" alt="Contact Us" className="w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(28,37,65,0.9) 0%, rgba(28,37,65,0.7) 100%)' }}></div>
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-testid="contact-hero">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight" data-testid="contact-hero-title">Let's Transform Your Space</h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-2xl mx-auto">Ready to get started? Contact us today for a free consultation and quote.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:1300ALPHAEPOXY" className="inline-flex items-center px-8 py-4 bg-red-600 text-white rounded-lg font-bold text-lg hover:bg-red-700 transition-all duration-200 shadow-lg" data-testid="contact-phone-btn">
                <PhoneCall className="w-6 h-6 mr-2" />0451 945 455
              </a>
              <a href="mailto:info@alphaepoxy.com.au" className="inline-flex items-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white rounded-lg font-bold text-lg hover:bg-white/20 transition-all duration-200" data-testid="contact-email-btn">
                <Mail className="w-6 h-6 mr-2" />Email Us
              </a>
            </div>
          </div>
        </section>

        {/* Contact Information */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              {[
                { icon: <PhoneCall className="w-8 h-8 text-white" />, title: "Phone", info: "0451 945 455", sub: "Mon-Fri: 7am - 6pm" },
                { icon: <Mail className="w-8 h-8 text-white" />, title: "Email", info: "info@alphaepoxy.com.au", sub: "24/7 Support" },
                { icon: <MapPin className="w-8 h-8 text-white" />, title: "Location", info: "Sydney, NSW", sub: "Serving all Sydney areas" }
              ].map((c, i) => (
                <Card key={i} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">{c.icon}</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{c.title}</h3>
                    <p className="text-gray-600 mb-2">{c.info}</p>
                    <p className="text-sm text-gray-500">{c.sub}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Contact Form */}
            <div className="max-w-3xl mx-auto">
              <Card className="border-none shadow-2xl">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Send Us a Message</h2>
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div><label className="block text-sm font-semibold text-gray-900 mb-2">Full Name *</label><Input name="name" value={formData.name} onChange={handleChange} placeholder="John Smith" required className="h-12" /></div>
                      <div><label className="block text-sm font-semibold text-gray-900 mb-2">Phone Number *</label><Input name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="04XX XXX XXX" required className="h-12" /></div>
                    </div>
                    <div><label className="block text-sm font-semibold text-gray-900 mb-2">Email Address *</label><Input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="john@example.com" required className="h-12" /></div>
                    <div><label className="block text-sm font-semibold text-gray-900 mb-2">Message</label><Textarea name="message" value={formData.message} onChange={handleChange} placeholder="Tell us about your project..." rows={6} className="resize-none" /></div>
                    <Button type="submit" size="lg" className="w-full bg-red-600 hover:bg-red-700 text-white font-bold h-14 text-lg" data-testid="contact-submit-btn"><Send className="w-5 h-5 mr-2" />Send Message</Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12"><h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Serving All of Sydney</h2><p className="text-xl text-gray-600 max-w-3xl mx-auto">From North Shore to Southern Sydney, Eastern Suburbs to Western Sydney — we come to you anywhere in Greater Sydney</p></div>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-red-600 h-[500px]"><iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d424143.94532033163!2d150.65179054788284!3d-33.84714479524214!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b129838f39a743f%3A0x3017d681632a850!2sSydney%20NSW%2C%20Australia!5e0!3m2!1sen!2s!4v1234567890123!5m2!1sen!2s" width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Sydney Service Area Map"></iframe></div>
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">Coverage Across Greater Sydney</h3>
                <p className="text-gray-600 text-lg mb-8">No matter where you are in Sydney, our expert team is ready to transform your space with premium epoxy flooring solutions.</p>
                <div className="grid grid-cols-2 gap-4 mb-8">{["Northern Beaches","North Shore","Inner West","Eastern Suburbs","Western Sydney","Southern Sydney","Hills District","Sutherland Shire","Parramatta Area","Canterbury-Bankstown","Blacktown","Penrith"].map((area,i)=>(<div key={i} className="flex items-center"><CheckCircle2 className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" /><span className="text-gray-700 font-medium">{area}</span></div>))}</div>
                <Card className="border-2 border-red-600/50 bg-red-50"><CardContent className="p-6"><h4 className="text-xl font-bold text-gray-900 mb-3">Free On-Site Assessment</h4><p className="text-gray-700 mb-4">We'll visit your location anywhere in Sydney for a free, no-obligation consultation and provide an accurate quote on the spot.</p><Button className="bg-red-600 hover:bg-red-700 text-white font-bold"><PhoneCall className="w-4 h-4 mr-2" />Book Your Free Visit</Button></CardContent></Card>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default Contact;
